import React, { useState, useEffect } from 'react'
import { Shield, Filter, Download, AlertCircle, CheckCircle, Info, Eye, FileText, Settings, Users, Lock } from 'lucide-react'
import { useTenant } from '../contexts/TenantContext'

interface AuditEvent {
  id: string
  timestamp: string
  tenantId: string
  eventType: string
  userId?: string
  userEmail?: string
  userName?: string
  userCompany?: string
  userIp: string
  userAgent: string
  resourceType?: string
  resourceId?: string
  resourceName?: string
  metadata?: Record<string, any>
  severity: 'info' | 'warning' | 'critical'
}

interface AuditSummary {
  totalEvents: number
  byEventType: Record<string, number>
  bySeverity: Record<string, number>
  uniqueUsers: number
  dateRange: {
    from: string
    to: string
  }
}

interface AuditTrailPageProps {
  embedded?: boolean
}

const AuditTrailPage: React.FC<AuditTrailPageProps> = ({ embedded = false }) => {
  const { currentTenant } = useTenant()
  const [events, setEvents] = useState<AuditEvent[]>([])
  const [summary, setSummary] = useState<AuditSummary | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [accessDenied, setAccessDenied] = useState(false)

  // Filters
  const [days, setDays] = useState(30)
  const [eventTypeFilter, setEventTypeFilter] = useState('')
  const [userEmailFilter, setUserEmailFilter] = useState('')
  const [severityFilter, setSeverityFilter] = useState('')

  useEffect(() => {
    if (currentTenant) {
      fetchAuditTrail()
    }
  }, [currentTenant, days, eventTypeFilter, userEmailFilter, severityFilter])

  const fetchAuditTrail = async () => {
    try {
      setLoading(true)
      const params = new URLSearchParams({
        tenantId: currentTenant!.id,
        days: days.toString()
      })

      if (eventTypeFilter) params.append('eventType', eventTypeFilter)
      if (userEmailFilter) params.append('userEmail', userEmailFilter)
      if (severityFilter) params.append('severity', severityFilter)

      const response = await fetch(`/api/audit-log?${params.toString()}`)

      if (!response.ok) {
        // Gracefully handle authentication/authorization errors by hiding component
        if (response.status === 401 || response.status === 403) {
          setAccessDenied(true)
          setLoading(false)
          return
        }

        throw new Error('Failed to fetch audit trail')
      }

      const data = await response.json()
      setEvents(data.events)
      setSummary(data.summary)
    } catch (err) {
      // Network errors - fail silently
      setAccessDenied(true)
    } finally {
      setLoading(false)
    }
  }

  const exportToCSV = () => {
    const headers = ['Timestamp', 'Event Type', 'User Email', 'User Company', 'Resource', 'IP Address', 'Severity']
    const rows = events.map(event => [
      new Date(event.timestamp).toLocaleString(),
      event.eventType,
      event.userEmail || 'N/A',
      event.userCompany || 'N/A',
      event.resourceName || event.resourceId || 'N/A',
      event.userIp,
      event.severity
    ])

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n')

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)
    link.setAttribute('href', url)
    link.setAttribute('download', `audit-trail-${new Date().toISOString().split('T')[0]}.csv`)
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }

  const getEventIcon = (eventType: string) => {
    switch (eventType) {
      case 'page_view':
        return <Eye className="h-4 w-4" />
      case 'document_download':
      case 'document_view':
        return <FileText className="h-4 w-4" />
      case 'login':
      case 'user_created':
        return <Users className="h-4 w-4" />
      case 'access_request':
        return <Lock className="h-4 w-4" />
      case 'settings_change':
      case 'content_update':
        return <Settings className="h-4 w-4" />
      default:
        return <Info className="h-4 w-4" />
    }
  }

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical':
        return 'text-red-600 bg-red-50'
      case 'warning':
        return 'text-yellow-600 bg-yellow-50'
      default:
        return 'text-blue-600 bg-blue-50'
    }
  }

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'critical':
        return <AlertCircle className="h-4 w-4" />
      case 'warning':
        return <AlertCircle className="h-4 w-4" />
      default:
        return <CheckCircle className="h-4 w-4" />
    }
  }

  if (loading && events.length === 0) {
    return (
      <div className={embedded ? "p-8" : "min-h-screen bg-gray-50 flex items-center justify-center"}>
        <div className="text-center">
          <Shield className="h-12 w-12 text-primary-600 mx-auto mb-4 animate-spin" />
          <p className="text-gray-600">Loading audit trail...</p>
        </div>
      </div>
    )
  }

  // Hide component gracefully when access is denied
  if (accessDenied) {
    return null
  }

  const content = (
    <div className={embedded ? "" : "max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8"}>
        {/* Summary Cards */}
        {summary && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <button
              onClick={() => {
                setSeverityFilter('')
                setEventTypeFilter('')
                setUserEmailFilter('')
              }}
              className="bg-white rounded-lg shadow p-6 hover:shadow-lg hover:border-blue-500 border-2 border-transparent transition-all cursor-pointer text-left"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Total Events</p>
                  <p className="text-2xl font-bold text-gray-900">{summary.totalEvents}</p>
                </div>
                <Info className="h-8 w-8 text-blue-500" />
              </div>
            </button>

            <div className="bg-white rounded-lg shadow p-6 opacity-75">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Unique Users</p>
                  <p className="text-2xl font-bold text-gray-900">{summary.uniqueUsers}</p>
                </div>
                <Users className="h-8 w-8 text-green-500" />
              </div>
            </div>

            <button
              onClick={() => setSeverityFilter('critical')}
              className={`bg-white rounded-lg shadow p-6 hover:shadow-lg hover:border-red-500 border-2 transition-all cursor-pointer text-left ${
                severityFilter === 'critical' ? 'border-red-500' : 'border-transparent'
              }`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Critical Events</p>
                  <p className="text-2xl font-bold text-gray-900">{summary.bySeverity.critical || 0}</p>
                </div>
                <AlertCircle className="h-8 w-8 text-red-500" />
              </div>
            </button>

            <button
              onClick={() => setSeverityFilter('warning')}
              className={`bg-white rounded-lg shadow p-6 hover:shadow-lg hover:border-yellow-500 border-2 transition-all cursor-pointer text-left ${
                severityFilter === 'warning' ? 'border-yellow-500' : 'border-transparent'
              }`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Warnings</p>
                  <p className="text-2xl font-bold text-gray-900">{summary.bySeverity.warning || 0}</p>
                </div>
                <AlertCircle className="h-8 w-8 text-yellow-500" />
              </div>
            </button>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-lg shadow p-6 mb-8">
          <div className="flex items-center mb-4">
            <Filter className="h-5 w-5 text-gray-600 mr-2" />
            <h2 className="text-lg font-semibold text-gray-900">Filters</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Time Period</label>
              <select
                value={days}
                onChange={(e) => setDays(parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="1">Last 24 hours</option>
                <option value="7">Last 7 days</option>
                <option value="30">Last 30 days</option>
                <option value="90">Last 90 days</option>
                <option value="365">Last year</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Event Type</label>
              <select
                value={eventTypeFilter}
                onChange={(e) => setEventTypeFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">All Events</option>
                <option value="page_view">Page Views</option>
                <option value="document_download">Document Downloads</option>
                <option value="document_view">Document Views</option>
                <option value="login">Logins</option>
                <option value="access_request">Access Requests</option>
                <option value="settings_change">Settings Changes</option>
                <option value="content_update">Content Updates</option>
                <option value="user_created">User Created</option>
                <option value="email_captured">Email Captured</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">User Email</label>
              <input
                type="text"
                value={userEmailFilter}
                onChange={(e) => setUserEmailFilter(e.target.value)}
                placeholder="Filter by email..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Severity</label>
              <select
                value={severityFilter}
                onChange={(e) => setSeverityFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">All Severities</option>
                <option value="info">Info</option>
                <option value="warning">Warning</option>
                <option value="critical">Critical</option>
              </select>
            </div>
          </div>
        </div>

        {/* Events Table */}
        {error ? (
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
            <AlertCircle className="h-12 w-12 text-red-600 mx-auto mb-4" />
            <p className="text-red-600">{error}</p>
          </div>
        ) : events.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <Shield className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Events Found</h3>
            <p className="text-gray-600">
              No audit events match the selected filters.
            </p>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Timestamp
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Event
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Resource
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    IP Address
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Severity
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {events.map((event) => (
                  <tr key={event.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {new Date(event.timestamp).toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center text-sm">
                        <span className="text-gray-600 mr-2">{getEventIcon(event.eventType)}</span>
                        <span className="text-gray-900">{event.eventType.replace('_', ' ')}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm">
                      <div className="text-gray-900">{event.userEmail || 'Anonymous'}</div>
                      {event.userCompany && (
                        <div className="text-gray-500 text-xs">{event.userCompany}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {event.resourceName || event.resourceId || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {event.userIp}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getSeverityColor(event.severity)}`}>
                        <span className="mr-1">{getSeverityIcon(event.severity)}</span>
                        {event.severity}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
  )

  if (embedded) {
    return (
      <div className="space-y-6">
        {/* Export Button */}
        <div className="flex justify-end">
          <button
            onClick={exportToCSV}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </button>
        </div>
        {content}
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center">
              <Shield className="h-8 w-8 text-primary-600" />
              <h1 className="ml-3 text-2xl font-bold text-gray-900">Audit Trail</h1>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={exportToCSV}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                <Download className="h-4 w-4 mr-2" />
                Export CSV
              </button>
              <a href="/admin" className="text-primary-600 hover:text-primary-700">← Back to Admin</a>
            </div>
          </div>
        </div>
      </header>

      {content}
    </div>
  )
}

export default AuditTrailPage
